'use client';
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useProductSearch } from 'frontastic/hooks/useSearch';
import { Sort, FacetFilter } from 'shared/types/query/ProductQuery';
import { SortAttributes } from 'frontastic/tastics/products/product-list/components/ProductListSort';
import { useParams } from 'next/navigation';

interface ProductSearchContextType {
  productsPaginatedResults: any;
  isLoading: boolean;
  sort: Sort;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  activeFacets: FacetFilter[];
  setActiveFacets: React.Dispatch<React.SetStateAction<FacetFilter[]>>;
}

const ProductSearchContext = createContext<ProductSearchContextType | undefined>(undefined);

export const ProductSearchProvider: React.FC<{ children: ReactNode; customSort?: Sort }> = ({
  children,
  customSort,
}) => {
  const [sort, setSort] = useState<Sort>(customSort ?? SortAttributes[0]);
  const [offset, setOffset] = useState<number>(0);
  const [activeFacets, setActiveFacets] = useState<FacetFilter[]>([]);
  const params = useParams();
  const categorySlug = params?.slug?.[1];

  const { productsPaginatedResults, isLoading } = useProductSearch({
    offset,
    categorySlug,
    activeFacets,
    sort,
  });

  return (
    <ProductSearchContext.Provider
      value={{
        productsPaginatedResults,
        isLoading,
        sort,
        setSort,
        offset,
        setOffset,
        activeFacets,
        setActiveFacets,
      }}
    >
      {children}
    </ProductSearchContext.Provider>
  );
};

export const useProductSearchContext = () => {
  const context = useContext(ProductSearchContext);
  if (context === undefined) {
    throw new Error('useProductSearchContext must be used within a ProductSearchProvider');
  }
  return context;
};
